/**
 * sizeUtils.js
 *
 * Single source of truth for size key mappings used across:
 *   - products.js  (normalizer, inventory helpers)
 *   - orders.js    (inventory deduction, calcItemTotal)
 *   - AdminManageInventory.jsx (stock grid)
 *
 * Key format convention:
 *   "frontend keys"  → lowercase strings used in sizePrices & cart quantities (e.g. 'small', 'xxl')
 *   "display names"  → human-readable strings used in the inventory matrix (e.g. 'Small', '2XL')
 */

// ─── Forward map: frontend key → display name ─────────────────────────────────
export const SIZE_KEY_TO_DISPLAY = {
    xxs:     'XXS',
    xs:      'XS',
    small:   'Small',
    medium:  'Medium',
    large:   'Large',
    xl:      'XL',
    xxl:     '2XL',
    xxxl:    '3XL',
    xxxxl:   '4XL',
    xxxxxl:  '5XL',
    xxxxxxl: '6XL',
    // Pass-through: display names map to themselves so either format can be used safely
    XXS:   'XXS',
    XS:    'XS',
    Small:  'Small',
    Medium: 'Medium',
    Large:  'Large',
    XL:    'XL',
    '2XL': '2XL',
    '3XL': '3XL',
    '4XL': '4XL',
    '5XL': '5XL',
    '6XL': '6XL',
};

// ─── Reverse map: display name → frontend key ─────────────────────────────────
export const SIZE_DISPLAY_TO_KEY = {
    'XXS':    'xxs',
    'XS':     'xs',
    'Small':  'small',
    'Medium': 'medium',
    'Large':  'large',
    'XL':     'xl',
    '2XL':    'xxl',
    '3XL':    'xxxl',
    '4XL':    'xxxxl',
    '5XL':    'xxxxxl',
    '6XL':    'xxxxxxl',
};

// ─── Ordered size lists ────────────────────────────────────────────────────────
export const ADULT_SIZES_DISPLAY = ['XXS', 'XS', 'Small', 'Medium', 'Large', 'XL', '2XL', '3XL', '4XL', '5XL', '6XL'];
export const KIDS_SIZES_DISPLAY  = ['XS', 'Small', 'Medium', 'Large', 'XL', '2XL'];

export const ADULT_SIZES_KEYS = ['xxs', 'xs', 'small', 'medium', 'large', 'xl', 'xxl', 'xxxl', 'xxxxl', 'xxxxxl', 'xxxxxxl'];
export const KIDS_SIZES_KEYS  = ['xs', 'small', 'medium', 'large', 'xl', 'xxl'];

export const SIZE_MAP_DISPLAY = { Adults: ADULT_SIZES_DISPLAY, Kids: KIDS_SIZES_DISPLAY };
export const SIZE_MAP_KEYS    = { Adults: ADULT_SIZES_KEYS,    Kids: KIDS_SIZES_KEYS    };

/**
 * Converts a sizePrices object from lowercase-key format to display-name-key format.
 * Safe to call on objects already in display-name format (idempotent).
 *
 * @param {Object} sizePrices - e.g. { small: 350, medium: 370 }
 * @returns {Object}          - e.g. { Small: 350, Medium: 370 }
 */
export function normalizeSizePricesToDisplayKeys(sizePrices) {
    if (!sizePrices || typeof sizePrices !== 'object') return {};
    const result = {};
    for (const [key, val] of Object.entries(sizePrices)) {
        const display = SIZE_KEY_TO_DISPLAY[key] || key;
        result[display] = val;
    }
    return result;
}
